// PipelineStatusStrip — Sanierung 03.07. (Phase 3, Frontend-Seite).
//
// Zeigt tote/veraltete Datenstrecken als rote/amber Pills direkt unter dem
// Header — auf JEDEM Tab. Quelle: GET /api/v1/pipelines/freshness (Befund
// des täglichen datenfrische_watch-Jobs). Grüne Strecken werden NICHT
// gerendert; sind alle ok, verschwindet die Leiste komplett — das Cockpit
// bleibt ruhig, solange die Daten stimmen.
//
// Motivation: EBICS + StB-IMAP waren wochenlang still tot und das Cockpit
// zeigte kommentarlos alte Zahlen. Diese Leiste macht "Zahl ist alt" so
// sichtbar wie die Zahl selbst.

import { useQuery } from "@tanstack/react-query";

import {
  FreshnessItem,
  FreshnessResponse,
  UnauthorizedError,
  fetchPipelinesFreshness,
} from "../lib/api";

const FIRMA_LABEL: Record<string, string> = {
  drainx: "DrainX",
  gundk: "G+K",
  infrax: "InfraX",
  xholding: "X-Holding",
};

// Kurzlabels — die Pills müssen einzeilig scanbar bleiben.
const DATENART_KURZ: Record<string, string> = {
  saldo_snapshot: "Bank",
  lohnlauf: "Lohn",
  bwa: "BWA",
  tooltime_snapshot: "Tooltime",
  outflow: "Ausgaben",
};

function pillText(item: FreshnessItem): string {
  const art = DATENART_KURZ[item.datenart] ?? item.datenart;
  const wer = item.firma ? ` ${FIRMA_LABEL[item.firma] ?? item.firma}` : "";
  if (item.status === "missing") return `${art}${wer}: nie Daten`;
  if (item.status === "unbekannt") return `${art}${wer}: ?`;
  return `${art}${wer}: ${item.alter_tage ?? "?"} T alt`;
}

function pillTitle(item: FreshnessItem): string {
  const stand = item.letzter_stand_iso
    ? new Date(item.letzter_stand_iso).toLocaleDateString("de-DE")
    : "nie";
  const seit = item.seit ? ` · rot seit ${item.seit}` : "";
  return `${item.label} — letzter Stand: ${stand} (Schwelle ${item.schwelle_tage} T)${seit}\n→ ${item.hinweis}`;
}

export default function PipelineStatusStrip() {
  const q = useQuery<FreshnessResponse, Error>({
    queryKey: ["pipelines-freshness"],
    queryFn: fetchPipelinesFreshness,
    staleTime: 10 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: (n, err) => !(err instanceof UnauthorizedError) && n < 1,
  });

  // Kein Befund / noch nie gelaufen / alles grün → nichts rendern.
  if (!q.data?.available) return null;
  const rot = q.data.items.filter(
    (i) => i.status === "stale" || i.status === "missing",
  );
  const unbekannt = q.data.items.filter((i) => i.status === "unbekannt");
  if (rot.length === 0 && unbekannt.length === 0) return null;

  return (
    <div
      role="status"
      aria-label={`${rot.length} Datenstrecken veraltet`}
      className="flex items-center gap-1.5 overflow-x-auto px-4 sm:px-6 py-1.5 border-b border-rose-500/20 bg-rose-950/20 [scrollbar-width:none]"
    >
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-rose-300/80">
        Datenstand
      </span>
      {rot.map((item) => (
        <span
          key={`${item.datenart}:${item.firma ?? "konzern"}`}
          title={pillTitle(item)}
          className={`shrink-0 cursor-help rounded-full border px-2 py-0.5 text-[10px] font-medium tabular-nums ${
            item.status === "missing"
              ? "border-rose-500/50 bg-rose-500/15 text-rose-200"
              : "border-amber-500/50 bg-amber-500/15 text-amber-200"
          }`}
        >
          {pillText(item)}
        </span>
      ))}
      {unbekannt.length > 0 && (
        <span
          title={unbekannt.map((i) => `${i.label}: ${i.detail ?? "Abfragefehler"}`).join("\n")}
          className="shrink-0 cursor-help rounded-full border border-slate-500/40 bg-slate-500/10 px-2 py-0.5 text-[10px] text-slate-300"
        >
          {unbekannt.length} nicht prüfbar
        </span>
      )}
    </div>
  );
}
